import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatSelect } from '@angular/material';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ReplaySubject, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { CommonService } from 'src/app/core/services/common.service';
import { ContractLotService } from '../../../../../../core/services/contract-lot.service';
import { DocumentMohaService } from '../../../../../../core/services/document-moha.service';
import FileUtils from '../../../../../../core/services/file-utils';
import { NoteDialogComponent, NoteDialogData } from '../standardizing-labor-contracts/note-dialog/note-dialog.component';
import { StandardizeContractListDialogComponent } from './standardize-contract-list-dialog/standardize-contract-list-dialog.component';
import { NotifyWarningCustomComponent } from 'src/app/shared/components/notify-warning-custom/notify-warning-custom.component';
import { Message } from 'src/app/core/class/message';


export interface StandardizeContractItem {
  id: number | string;
  stt: number;
  codeName: string;
  contractType: string;
  parties: string;
  statusCksNsdld: string;
  statusCksNld: string;
  statusSupplementHDLD: string;
  statusStandardized: string;
  statusReview?: string;
  reviewErrors?: any[];
}

export interface StandardizeReviewErrorDetail {
  condition: string;
  message: string;
}

export interface StandardizeSearchForm {
  nameOrCode: string;
  serviceType: number | string | null;
  statusStandardized: string | null;
  statusDigitalCert: string | null;
  statusDigitalCertPersonal: string | null;
  updateInfoProperties: string | null;
}

export class Paginate {
  currentPage: number;
  pageSize: number;
  totalRow: number;
  totalPage: number;
  totalPageArray: [];
  no: [];
}

@Component({
  selector: 'app-labor-contract-standardize-execute',
  templateUrl: './labor-contract-standardize-execute.component.html',
  styleUrls: ['./labor-contract-standardize-execute.component.scss']
})
export class LaborContractStandardizeExecuteComponent implements OnInit, OnDestroy {

  private readonly i18nPrefix = 'labor_contract_standardize';

  private readonly standardizeAllPageSizeParamCode = 'size_contract_standardized';
  standardizeAllPageSize = 50;

  // ==== Cột hiển thị bảng ====
  displayedColumns: string[] = [
    'check-col',
    'stt',
    'codeName',
    'contractType',
    'parties',
    'statusCksNsdld',
    'statusCksNld',
    'statusSupplementHDLD',
    'statusStandardized',
    'action'
  ];
  standardizeProcessDisplayedColumns: string[] = [
    'stt',
    'codeName',
    'contractType',
    'parties',
    'statusCksNsdld',
    'statusCksNld',
    'statusSupplementHDLD',
    'statusStandardized',
    'note'
  ];

  // ==== Form tìm kiếm dùng trực tiếp tên field của API ====
  documents: StandardizeSearchForm = {
    nameOrCode: '',
    serviceType: null,
    statusStandardized: null,
    statusDigitalCert: null,
    statusDigitalCertPersonal: null,
    updateInfoProperties: null
  };

  // Danh sách loại hợp đồng của doanh nghiệp
  serviceTypes: any[] = [];
  serviceTypesFilterCtrl: FormControl = new FormControl();
  filteredServiceTypes: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  checkFocus = true;
  @ViewChild('serviceTypeSelect', {static: false}) serviceTypeSelect: MatSelect;
  @ViewChild('reviewErrorDialog', {static: false}) reviewErrorDialog: TemplateRef<any>;
  private onDestroySubject = new Subject<void>();

  // Danh sách điều kiện tìm kiếm
  cksStatusOptions: { value: string; label: string }[] = [
    { value: 'INVALID', label: this.translate('status_labels.invalid') },
    { value: 'VALID', label: this.translate('status_labels.valid') }
  ];
  supplementStatusOptions: { value: string; label: string }[] = [
    { value: 'INVALID', label: this.translate('status_labels.invalid') },
    { value: 'VALID', label: this.translate('status_labels.valid') }
  ];
  standardizeStatusOptions: { value: string; label: string }[] = [
    { value: 'NOT_STANDARDIZED', label: this.translate('status_labels.not_standardized') },
    { value: 'LACKING_CONDITIONS', label: this.translate('status_labels.lacking_conditions') },
    { value: 'WAIT', label: this.translate('status_labels.processing_condition') },
    { value: 'DONE', label: this.translate('status_labels.standardized') }
  ];

  // Bật/tắt khối tìm kiếm nâng cao
  showAdvancedSearch = false;

  // Ánh xạ mã trạng thái (BE trả) -> nhãn hiển thị
  // TODO: xác nhận lại chính xác từng nhãn/màu với BA-PM, đây là bộ mặc định hợp lý theo mock UI
  statusLabelMap: { [key: string]: string } = {
    VALID: this.translate('status_labels.valid'),
    INVALID: this.translate('status_labels.invalid'),
    DONE: this.translate('status_labels.done'),
    NOT_STANDARDIZED: this.translate('status_labels.not_standardized'),
    STANDARDIZED: this.translate('status_labels.standardized'),
    LACKING_CONDITIONS: this.translate('status_labels.lacking_conditions'),
    ERROR: this.translate('status_labels.error'),
    PROCESSING: this.translate('status_labels.processing'),
    WAIT: this.translate('status_labels.wait')
  };

  statusClassMap: { [key: string]: string } = {
    VALID: '#027A48',
    DONE: '#027A48',
    STANDARDIZED: '#027A48',
    INVALID: '#A90024',
    ERROR: '#A90024',
    NOT_STANDARDIZED: '#AE7008',
    LACKING_CONDITIONS: '#AE7008',
    PROCESSING: '#027A48',
    WAIT: '#027A48'
  };

  standardizeStatusLabelMap: { [key: string]: string } = {
    NOT_STANDARDIZED: this.translate('status_labels.not_standardized'),
    LACKING_CONDITIONS: this.translate('status_labels.lacking_conditions'),
    WAIT: this.translate('status_labels.processing_condition'),
    DONE: this.translate('status_labels.standardized')
  };

  // ==== Dữ liệu bảng ====
  dataSource: { data: StandardizeContractItem[] } = { data: [] };
  selection = new SelectionModel<number | string>(true, []);
  selectedContractItemMap: { [key: string]: StandardizeContractItem } = {};
  isLoading = false;
  isLoadingStandardizeAll = false;

  // ==== Tiến trình chuẩn hóa ====
  showStandardizeProcess = false;
  currentStandardizeStep = 0;
  selectedSupplementFile: File = null;
  isSupplementFileDragging = false;
  isResultSummaryExpanded = true;
  isSubmittingSupplementFile = false;
  isDownloadingSupplementTemplate = false;
  supplementResult = {
    totalRecords: 0,
    validRecords: 0,
    invalidRecords: 0,
    progress: 0,
    status: 'WAIT'
  };
  standardizeLotId = '';
  importLotCode = '';
  contractLotImportFileName = '';
  contractLotDetails: any[] = [];
  isDownloadingSupplementResult = false;
  private progressPollingInterval: any = null;
  private isProgressRequesting = false;
  selectedStandardizeContractIds: Array<number | string> = [];
  standardizeProcessContracts: StandardizeContractItem[] = [];
  standardizeProcessDataSource: { data: StandardizeContractItem[] } = { data: [] };
  isStandardizeProcessLoading = false;
  selectedReviewErrorContract: StandardizeContractItem = null;
  selectedReviewErrorDetails: StandardizeReviewErrorDetail[] = [];
  standardizeProcessPaginate: Paginate = {
    currentPage: 0,
    pageSize: 10,
    totalRow: 0,
    totalPage: 0,
    totalPageArray: [],
    no: []
  };

  // ==== Phân trang ====
  paginate: Paginate = {
    currentPage: 0,
    pageSize: 10,
    totalRow: 0,
    totalPage: 0,
    totalPageArray: [],
    no: []
  };

  constructor(
    public commonService: CommonService,
    private toastr: ToastrService,
    private translateService: TranslateService,
    private documentMohaService: DocumentMohaService,
    private contractLotService: ContractLotService,
    private dialog: MatDialog,
    private router: Router,
    private message: Message
  ) { }

  private translate(key: string, params?: any): string {
    return this.translateService.instant(this.i18nPrefix + '.' + key, params);
  }

  ngOnInit(): void {
    this.serviceTypesFilterCtrl.valueChanges
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(() => {
        this.commonService.filterCertificateType(
          this.serviceTypes,
          this.serviceTypesFilterCtrl,
          this.filteredServiceTypes
        );
      });
    this.getStandardizeAllPageSize();
    this.getServiceTypeByEnterprise();
    this.onSearch();
  }

  ngOnDestroy(): void {
    this.stopProgressPolling();
    this.onDestroySubject.next();
    this.onDestroySubject.complete();
  }

  getServiceTypeByEnterprise(): void {
    const enterpriseId = localStorage.getItem('enterpriseId');
    this.commonService.getEnterpriseServiceType(enterpriseId).subscribe(res => {
      if (res && res.data) {
        this.serviceTypes = res.data;
        this.filteredServiceTypes.next(this.serviceTypes.slice());
        this.documents.serviceType = this.documents.serviceType
          ? Number(this.documents.serviceType)
          : null;
      }
    });
  }

  private getStandardizeAllPageSize(): void {
    this.documentMohaService.getApparamInfo({
      code: this.standardizeAllPageSizeParamCode
    })
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(
        (res: any) => {
          const configuredLimit = Number(res && res.data ? res.data.value : null);
          if (configuredLimit > 0 && Math.floor(configuredLimit) === configuredLimit) {
            this.standardizeAllPageSize = configuredLimit;
          }
        },
        (err: any) => {
          console.error(this.translate('load_limit_error'), err);
        }
      );
  }

  // ================= TÌM KIẾM =================
  toggleAdvancedSearch(): void {
    this.showAdvancedSearch = !this.showAdvancedSearch;
  }

  onSearch(): void {
    this.selection.clear();
    this.selectedContractItemMap = {};
    this.loadData(0, this.paginate.pageSize);
  }

  resetSearchForm(): void {
    this.documents = {
      nameOrCode: '',
      serviceType: null,
      statusStandardized: null,
      statusDigitalCert: null,
      statusDigitalCertPersonal: null,
      updateInfoProperties: null
    };
    this.onSearch();
  }

  /**
   * Tạo request tìm kiếm từ model có cùng tên field với API.
   */
  private buildFilterRequest(page: number, pageSize: number): any {
    return {
      nameOrCode: this.documents.nameOrCode || '',
      serviceType: this.documents.serviceType || null,
      statusDigitalCert: this.showAdvancedSearch && this.documents.statusDigitalCert
        ? this.documents.statusDigitalCert
        : '',
      statusStandardized: this.documents.statusStandardized || '',
      statusDigitalCertPersonal: this.showAdvancedSearch && this.documents.statusDigitalCertPersonal
        ? this.documents.statusDigitalCertPersonal
        : '',
      updateInfoProperties: this.showAdvancedSearch && this.documents.updateInfoProperties
        ? this.documents.updateInfoProperties
        : '',
      contractIds: [],
      page,
      pageSize
    };
  }

  // ================= LOAD DỮ LIỆU =================
  loadData(page: number = 0, pageSize: number = 10): void {
    this.isLoading = true;
    const request = this.buildFilterRequest(page, pageSize);

    this.documentMohaService.filterStandardizedContractsExecute(request)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe(
        (res: any) => this.handleFetchDataSuccess(res),
        (err: any) => this.handleFetchDataError(err)
      );
  }

  private handleFetchDataSuccess(res: any): void {
    // Cấu trúc response thực tế (đã xác nhận qua console log):
    // { code, message, success, data: { currentPage, pageSize, totalRow, dataRow: [ {...}, ... ] } }
    const data = res && res.data ? res.data : {};
    const list: any[] = data.dataRow ? data.dataRow : [];
    const total: number = (typeof data.totalRow === 'number') ? data.totalRow : list.length;

    const currentPage = typeof data.currentPage === 'number' ? data.currentPage : 0;
    const pageSize = typeof data.pageSize === 'number' && data.pageSize > 0
      ? data.pageSize
      : this.paginate.pageSize;
    const totalPage = Math.ceil(total / pageSize);

    this.paginate = {
      currentPage,
      pageSize,
      totalRow: total,
      totalPage,
      totalPageArray: this.counter(totalPage, 1),
      no: this.counter(pageSize, currentPage * pageSize + 1)
    };
    this.dataSource.data = list.map((item: any, index: number) => this.mapItem(item, index));
  }

  private handleFetchDataError(err: any): void {
    this.dataSource.data = [];
    this.paginate = {
      ...this.paginate,
      totalRow: 0,
      totalPage: 0,
      totalPageArray: [],
      no: []
    };
    // TODO: hiển thị thông báo lỗi cho người dùng (toast/snackbar) theo cơ chế chung của dự án
  }

  /**
   * Map 1 phần tử response API sang model hiển thị của bảng.
   * Tên field lấy theo bảng mô tả response trong tài liệu API:
   * name, allName, serviceTypeName, statusDigitalCert, statusDigitalCertPersonal,
   * statusStandardized, statusUpdateInfoProperties.
   */
  private getResponseField(item: any, fieldName: string): any {
    if (item[fieldName] !== undefined) {
      return item[fieldName];
    }

    const matchingKey = Object.keys(item).find(key => key.trim() === fieldName);
    return matchingKey ? item[matchingKey] : null;
  }

  private mapItem(item: any, index: number): StandardizeContractItem {
    return {
      id: item.id,
      stt: this.paginate.currentPage * this.paginate.pageSize + index + 1,
      codeName: item.name,
      contractType: item.serviceTypeName,
      parties: item.allName,
      statusCksNsdld: item.statusDigitalCert,
      statusCksNld: this.getResponseField(item, 'statusDigitalCertPersonal'),
      statusSupplementHDLD: item.statusUpdateInfoProperties,
      statusStandardized: item.statusStandardized,
      statusReview: this.getResponseField(item, 'statusReview'),
      reviewErrors: this.getReviewErrors(item)
    };
  }

  // ================= CHỌN DÒNG =================
  isAllSelected(): boolean {
    const selectedIds = this.selection.selected;
    const pageIds = this.dataSource.data.map(item => item.id);
    return pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));
  }

  masterToggle(): void {
    const pageIds = this.dataSource.data.map(item => item.id);
    if (this.isAllSelected()) {
      this.selection.deselect(...pageIds);
      pageIds.forEach(id => delete this.selectedContractItemMap[String(id)]);
    } else {
      this.selection.select(...pageIds);
      this.dataSource.data.forEach(item => {
        this.selectedContractItemMap[String(item.id)] = item;
      });
    }
  }

  addListRecord(element: StandardizeContractItem): void {
    this.selection.toggle(element.id);
    if (this.selection.isSelected(element.id)) {
      this.selectedContractItemMap[String(element.id)] = element;
    } else {
      delete this.selectedContractItemMap[String(element.id)];
    }
  }

  // ================= PHÂN TRANG =================
  onFilterData(page: number = 0, pageSize: number = 10): void {
    if (pageSize !== this.paginate.pageSize) {
      this.selection.clear();
      this.selectedContractItemMap = {};
    }
    this.loadData(page, pageSize);
  }

  counter(index: number, from: number): any {
    return new Array(index).fill(0).map((item, position) => position + from);
  }

  // ================= TRẠNG THÁI HIỂN THỊ =================
  getStatusLabel(code: string): string {
    if (!code) {
      return '-';
    }
    return this.statusLabelMap[code] ? this.statusLabelMap[code] : code;
  }

  getStatusClass(code: string): string {
    if (!code) {
      return 'status-empty';
    }
    return this.statusClassMap[code] ? this.statusClassMap[code] : '#AE7008';
  }

  getStatusTagColor(status: string): string {
    switch (status) {
      case 'DONE':
      case 'VALID':
        return 'green';
      case 'WAIT':
      case 'PROCESSING':
      case 'LACKING_CONDITIONS':
        return 'blue';
      case 'ERROR':
      case 'INVALID':
      case 'NOT_STANDARDIZED':
        return 'red';
      default:
        return '';
    }
  }

  getStandardizeStatusLabel(code: string): string {
    if (!code) {
      return '-';
    }
    return this.standardizeStatusLabelMap[code]
      ? this.standardizeStatusLabelMap[code]
      : code;
  }

  // ================= HÀNH ĐỘNG =================
  onView(element: StandardizeContractItem): void {
    if (!element || !element.id) {
      return;
    }

    this.router.navigate(['/home-enterprise/view-document-moha/' + element.id], {
      queryParams: {
        goFrom: 'standardizedContractExecute',
        routeLink: '/home-enterprise/standardizing-labor-contracts',
        tab: 1
      }
    });
  }

  onStandardizeOne(element: StandardizeContractItem): void {
    if (!element || !element.id) {
      return;
    }

    this.selection.clear();
    this.selectedContractItemMap = {};
    this.selection.select(element.id);
    this.selectedContractItemMap[String(element.id)] = element;
    this.onStandardizeSelected();
  }

  openNoteDialog(row: any): void {
    this.dialog.open(NoteDialogComponent, {
      width: '560px',
      panelClass: 'note-dialog-panel',
      data: { contractId: row.id } as unknown as NoteDialogData,
    });
  }

  onStandardizeSelected(): void {
    if (this.isLoadingStandardizeAll) {
      return;
    }

    if (!this.selection.hasValue()) {
      this.toastr.error(this.translate('messages.select_contract_required'));
      return;
    }

    let selectedIds = [...this.selection.selected];
    let selectedContracts = selectedIds
      .map(id => this.selectedContractItemMap[String(id)])
      .filter(item => !!item);

    // if (selectedIds.length > this.standardizeAllPageSize) {
    //   this.toastr.error(
    //     this.translate('messages.max_contract_limit', { max: this.standardizeAllPageSize })
    //   );
    //   return;
    // }

    if (selectedContracts.filter(contract => contract.statusStandardized === 'LACKING_CONDITIONS').length === 0) {
      const popup = this.dialog.open(NotifyWarningCustomComponent, {
        width: '600px',
        data: {
          content: this.commonService.format(this.message.getMessage('messages.no_standardized_contract')),
        }
      });
      return
    } else {
      if (selectedContracts.filter(contract => contract.statusStandardized === 'LACKING_CONDITIONS').length > this.standardizeAllPageSize) {
        selectedContracts = selectedContracts.slice(0, this.standardizeAllPageSize);
      }
    }


    this.openStandardizeContractListDialog(selectedContracts);
  }

  private openStandardizeContractListDialog(
    contracts: StandardizeContractItem[]
  ): void {
    const dialogRef = this.dialog.open(StandardizeContractListDialogComponent, {
      width: '805px',
      maxWidth: '95vw',
      panelClass: 'standardize-contract-list-dialog-panel',
      disableClose: true,
      data: {
        contracts,
        maxContracts: this.standardizeAllPageSize
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(confirmed => {
        if (confirmed && confirmed.length > 0) {
          this.startStandardizeProcess(confirmed);
        }
      });
  }

  private startStandardizeProcess(selectedIds: Array<number | string>): void {
    this.stopProgressPolling();
    const ids = [...selectedIds];
    this.selectedStandardizeContractIds = ids;
    this.standardizeProcessContracts = [];
    this.standardizeProcessDataSource.data = [];
    this.standardizeProcessPaginate = {
      ...this.standardizeProcessPaginate,
      currentPage: 0,
      totalRow: 0,
      totalPage: 0,
      totalPageArray: [],
      no: []
    };
    this.currentStandardizeStep = 0;
    this.selectedSupplementFile = null;
    this.isResultSummaryExpanded = true;
    this.isSubmittingSupplementFile = false;
    this.standardizeLotId = '';
    this.importLotCode = '';
    this.contractLotImportFileName = '';
    this.contractLotDetails = [];
    this.isDownloadingSupplementResult = false;
    this.supplementResult = {
      totalRecords: 0,
      validRecords: 0,
      invalidRecords: 0,
      progress: 0,
      status: 'WAIT'
    };
    this.showStandardizeProcess = true;
  }

  openSupplementFilePicker(fileInput: HTMLInputElement): void {
    fileInput.click();
  }

  onSupplementFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;
    this.setSupplementFile(file);
    input.value = '';
  }

  onSupplementFileDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isSupplementFileDragging = true;
  }

  onSupplementFileDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isSupplementFileDragging = false;
  }

  onSupplementFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isSupplementFileDragging = false;

    const file = event.dataTransfer && event.dataTransfer.files.length
      ? event.dataTransfer.files[0]
      : null;
    this.setSupplementFile(file);
  }

  private setSupplementFile(file: File): void {
    if (!file) {
      return;
    }

    const isXlsxFile = /\.xlsx$/i.test(file.name);
    if (!isXlsxFile) {
      this.toastr.error(this.translate('messages.only_xlsx'));
      return;
    }

    const maxFileSize = 10 * 1024 * 1024;
    if (file.size > maxFileSize) {
      this.toastr.error(this.translate('messages.file_too_large'));
      return;
    }

    this.selectedSupplementFile = file;
  }

  removeSupplementFile(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedSupplementFile = null;
  }

  cancelStandardizeProcess(): void {
    this.stopProgressPolling();
    this.showStandardizeProcess = false;
    this.currentStandardizeStep = 0;
    this.selectedSupplementFile = null;
    this.isSubmittingSupplementFile = false;
    this.standardizeLotId = '';
    this.importLotCode = '';
    this.contractLotImportFileName = '';
    this.contractLotDetails = [];
    this.isDownloadingSupplementResult = false;
    this.selectedStandardizeContractIds = [];
    this.isStandardizeProcessLoading = false;
    this.standardizeProcessContracts = [];
    this.standardizeProcessDataSource.data = [];
    this.onSearch();
  }

  async submitSupplementFile(): Promise<void> {
    if (!this.selectedSupplementFile) {
      this.toastr.error(this.translate('messages.select_file_required'));
      return;
    }

    if (this.isSubmittingSupplementFile) {
      return;
    }

    const contractIds = [...this.selectedStandardizeContractIds];
    if (!contractIds.length) {
      this.toastr.error(this.translate('messages.contract_not_found'));
      return;
    }

    this.isSubmittingSupplementFile = true;
    try {
      const fileDataUrl = await this.commonService.getBase64(this.selectedSupplementFile) as string;
      const base64SeparatorIndex = fileDataUrl.indexOf(',');
      const importFileBase64 = base64SeparatorIndex >= 0
        ? fileDataUrl.substring(base64SeparatorIndex + 1)
        : fileDataUrl;

      this.documentMohaService.createStandardized({
        contractIds,
        importFileBase64,
        importFileName: this.selectedSupplementFile.name
      })
        .pipe(finalize(() => this.isSubmittingSupplementFile = false))
        .subscribe(
          (res: any) => {
            if (!res || res.success === false) {
              this.toastr.error(res && res.message
                ? res.message
                : this.translate('messages.supplement_failed'));
              return;
            }

            this.setStandardizeLotId(res.data);
            if (!this.standardizeLotId) {
              this.toastr.error(this.translate('messages.lot_id_not_received'));
              return;
            }
            this.currentStandardizeStep = 1;
            this.loadStandardizeProcessData(0, this.standardizeProcessPaginate.pageSize);
            this.startProgressPolling();
          },
          () => this.toastr.error(this.translate('messages.supplement_failed'))
        );
    } catch (error) {
      this.isSubmittingSupplementFile = false;
      this.toastr.error(this.translate('messages.file_read_error'));
    }
  }

  get supplementResultFileName(): string {
    const fileName = 'BSTT_HDLD_ketqua.xlsx';
    return fileName;
  }

  get isSupplementProcessDone(): boolean {
    return String(this.supplementResult.status || '').toUpperCase() === 'DONE'
      && this.supplementResult.progress >= 100;
  }

  downloadSupplementResult(): void {
    if (!this.isSupplementProcessDone || this.isDownloadingSupplementResult) {
      return;
    }

    if (!this.standardizeLotId) {
      this.toastr.error(this.translate('messages.result_file_not_found'));
      return;
    }

    this.isDownloadingSupplementResult = true;
    this.contractLotService.getResultFile(this.standardizeLotId)
      .pipe(finalize(() => this.isDownloadingSupplementResult = false))
      .subscribe(
        file => {
          try {
            FileUtils.downLoadFile(
              file,
              'application/octet-stream',
              'BSTT_HDLD_ketqua.xlsx'
            );
          } catch (error) {
            this.toastr.error(this.translate('messages.result_download_failed'));
          }
        },
        () => this.toastr.error(this.translate('messages.result_download_failed'))
      );
  }

  toggleResultSummary(): void {
    this.isResultSummaryExpanded = !this.isResultSummaryExpanded;
  }

  returnToStandardizeMenu(): void {
    this.cancelStandardizeProcess();
  }

  onStandardizeProcessPageChange(page: number = 0, pageSize: number = 10): void {
    this.loadStandardizeProcessData(page, pageSize);
  }

  getProcessStatusClass(code: string): string {
    return code === 'PROCESSING' ? 'status-processing' : this.getStatusClass(code);
  }

  getSupplementProcessStatusLabel(code: string): string {
    return code === 'PROCESSING'
      ? this.translate('status_labels.processing_supplement')
      : this.getStatusLabel(code);
  }

  getStandardizeProcessStatusLabel(code: string): string {
    return code === 'PROCESSING'
      ? this.translate('status_labels.processing_condition')
      : this.getStandardizeStatusLabel(code);
  }

  hasReviewError(element: StandardizeContractItem): boolean {
    if (!element) {
      return false;
    }
    const isReviewed = String(element.statusStandardized || '').toUpperCase() !== 'DONE';
    return isReviewed
  }

  private getReviewErrors(item: any): any[] {
    const errors = this.getResponseField(item, 'reviewErrors')
      || this.getResponseField(item, 'reviewErrorDetails')
      || this.getResponseField(item, 'errorDetails')
      || this.getResponseField(item, 'errors');

    if (!errors) {
      return [];
    }
    return Array.isArray(errors) ? errors : [errors];
  }

  private setStandardizeLotId(responseData: string): void {
    this.standardizeLotId = responseData || '';
  }

  /**
   * Lấy thông tin lô ngay sau khi tạo, sau đó polling mỗi 5 giây cho tới khi xử lý xong.
   */
  private startProgressPolling(): void {
    this.stopProgressPolling();

    if (!this.standardizeLotId || this.isSupplementProcessDone) {
      return;
    }

    this.progressPollingInterval = setInterval(
      () => this.refreshStandardizeContractLot(),
      5000
    );
    this.refreshStandardizeContractLot();
  }

  private stopProgressPolling(): void {
    if (this.progressPollingInterval !== null) {
      clearInterval(this.progressPollingInterval);
      this.progressPollingInterval = null;
    }
  }

  private refreshStandardizeContractLot(): void {
    if (!this.standardizeLotId || this.isProgressRequesting) {
      return;
    }

    const requestedLotId = this.standardizeLotId;
    this.isProgressRequesting = true;
    this.contractLotService.getContractLotById(requestedLotId)
      .pipe(
        takeUntil(this.onDestroySubject),
        finalize(() => this.isProgressRequesting = false)
      )
      .subscribe(
        (res: any) => {
          if (requestedLotId !== this.standardizeLotId || !res || !res.data) {
            return;
          }

          this.applyContractLotResult(res.data);
          if (this.isSupplementProcessDone) {
            this.stopProgressPolling();
            this.loadStandardizeProcessData(
              this.standardizeProcessPaginate.currentPage,
              this.standardizeProcessPaginate.pageSize
            );
          }
        },
        () => {
          // Giữ polling để tự thử lại ở chu kỳ kế tiếp khi API lỗi tạm thời.
        }
      );
  }

  private applyContractLotResult(contractLot: any): void {
    const details = Array.isArray(contractLot.contractDetails)
      ? contractLot.contractDetails
      : [];
    const successRecords = details.filter(item =>
      String(item.status || '').toUpperCase() === 'DONE'
    ).length;
    const failedRecords = details.filter(item =>
      String(item.status || '').toUpperCase() === 'ERROR'
    ).length;
    const completedRecords = successRecords + failedRecords;
    const status = String(contractLot.status || 'PROCESSING').toUpperCase();
    const progress = details.length > 0
      ? Math.round(completedRecords / details.length * 100)
      : (status === 'DONE' ? 100 : 0);

    this.importLotCode = contractLot.code || '';
    this.contractLotImportFileName = contractLot.importFileName || '';
    this.contractLotDetails = details;
    this.supplementResult = {
      totalRecords: details.length,
      validRecords: successRecords,
      invalidRecords: failedRecords,
      progress: Math.min(100, Math.max(0, progress)),
      status
    };
  }

  private getContractLotErrors(element: StandardizeContractItem): any[] {
    if (!element || !this.contractLotDetails.length) {
      return [];
    }

    const elementCodeName = String(element.codeName || '').trim().toLowerCase();

    return this.contractLotDetails
      .filter(detail => String(detail.status || '').toUpperCase() === 'ERROR')
      .filter(detail => {
        const detailCode = String(detail.contractCode || '').trim().toLowerCase();
        const detailName = String(detail.contractName || '').trim().toLowerCase();
        return String(detail.id) === String(element.id)
          || (!!detailCode && elementCodeName.indexOf(detailCode) >= 0)
          || (!!detailName && elementCodeName.indexOf(detailName) >= 0);
      })
      .map(detail => ({
        conditionName: this.translate('review_error_dialog.condition_supplement'),
        errorMessage: detail.errorMessage || '-'
      }));
  }

  private loadStandardizeProcessData(page: number = 0, pageSize: number = 10): void {
    if (!this.selectedStandardizeContractIds.length) {
      this.standardizeProcessContracts = [];
      this.standardizeProcessDataSource.data = [];
      return;
    }

    const request = {
      ...this.buildFilterRequest(page, pageSize),
      contractIds: [...this.selectedStandardizeContractIds]
    };

    this.isStandardizeProcessLoading = true;
    this.standardizeProcessContracts = [];
    this.standardizeProcessDataSource.data = [];
    this.documentMohaService.filterStandardizedContractsExecute(request)
      .pipe(finalize(() => this.isStandardizeProcessLoading = false))
      .subscribe(
        (res: any) => {
          const data = res && res.data ? res.data : {};
          const list: any[] = data.dataRow ? data.dataRow : [];
          const totalRow = typeof data.totalRow === 'number' ? data.totalRow : list.length;
          const currentPage = typeof data.currentPage === 'number' ? data.currentPage : page;
          const responsePageSize = typeof data.pageSize === 'number' && data.pageSize > 0
            ? data.pageSize
            : pageSize;
          const totalPage = Math.ceil(totalRow / responsePageSize);

          this.standardizeProcessPaginate = {
            currentPage,
            pageSize: responsePageSize,
            totalRow,
            totalPage,
            totalPageArray: this.counter(totalPage, 1),
            no: this.counter(responsePageSize, currentPage * responsePageSize + 1)
          };
          this.standardizeProcessContracts = list.map((item: any, index: number) => ({
            ...this.mapItem(item, index),
            stt: currentPage * responsePageSize + index + 1
          }));
          this.standardizeProcessDataSource.data = this.standardizeProcessContracts;
        },
        () => {
          this.standardizeProcessContracts = [];
          this.standardizeProcessDataSource.data = [];
          this.standardizeProcessPaginate = {
            ...this.standardizeProcessPaginate,
            currentPage: page,
            pageSize,
            totalRow: 0,
            totalPage: 0,
            totalPageArray: [],
            no: []
          };
        }
      );
  }

  onStandardizeAll(): void {
    if (this.isLoadingStandardizeAll) {
      return;
    }

    const request = {
      nameOrCode: null,
      serviceType: null,
      statusStandardized: 'LACKING_CONDITIONS',
      statusDigitalCert: null,
      statusDigitalCertPersonal: null,
      updateInfoProperties: null,
      contractIds: [],
      page: 0,
      pageSize: this.standardizeAllPageSize
    };
    this.isLoadingStandardizeAll = true;

    this.documentMohaService.filterStandardizedContractsExecute(request)
      .pipe(
        finalize(() => this.isLoadingStandardizeAll = false),
        takeUntil(this.onDestroySubject)
      )
      .subscribe(
        (res: any) => {
          const data = res && res.data ? res.data : {};
          const list: any[] = data.dataRow ? data.dataRow : [];
          const contracts = list
            .slice(0, this.standardizeAllPageSize)
            .map((item: any, index: number) => ({
              ...this.mapItem(item, index),
              stt: index + 1
            }))
            .filter((item: StandardizeContractItem) => item.id !== null && item.id !== undefined);

          if (!contracts.length) {
            this.toastr.error(this.translate('messages.no_contract_to_standardize'));
            return;
          }

          const contractIds = contracts.map(item => item.id);
          this.openStandardizeContractListDialog(contracts);
        },
        () => {
          this.toastr.error(this.translate('messages.load_contracts_failed'));
        }
      );
  }

  downloadSupplementTemplate(): void {
    const contractIds = [...this.selectedStandardizeContractIds];
    if (!contractIds.length) {
      this.toastr.error(this.translate('messages.select_contract_for_template'));
      return;
    }

    this.isDownloadingSupplementTemplate = true;
    this.documentMohaService.generateImportFileStandardized({ contractIds })
      .pipe(finalize(() => this.isDownloadingSupplementTemplate = false))
      .subscribe(
        file => FileUtils.downLoadFile(
          file,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'BSTT_HDLD.xlsx'
        ),
        () => this.toastr.error(this.translate('messages.template_download_failed'))
      );
  }
}
